'use client';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import React, { useState } from 'react';

import { type ShippingAddressFields } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

const ShippingAddressTextInput: React.FC<{
  fieldEnteredFieldName: string;
  fieldEnteredOrderNumber: number;
  format?: RegExp;
  formatErrorMessage?: string;
  id: string;
  label: string;
  onBlurAction?: (id: string) => void;
  placeholder?: string;
  required?: boolean;
  setShippingAddressFields: React.Dispatch<
    React.SetStateAction<ShippingAddressFields>
  >;
  shippingAddressFields: ShippingAddressFields;
}> = ({
  fieldEnteredFieldName,
  fieldEnteredOrderNumber,
  format,
  formatErrorMessage,
  id,
  label,
  onBlurAction,
  placeholder,
  required,
  setShippingAddressFields,
  shippingAddressFields,
}) => {
  const [hasBeenInteractedWith, setHasBeenInteractedWith] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  let hasFormatError =
    format?.test(shippingAddressFields[fieldEnteredFieldName]) ?? false;

  const canShowAnError =
    (hasFormatError && shippingAddressFields[id].trim() !== '') ||
    (hasBeenInteractedWith && !isFocused);

  const showRequiredError =
    canShowAnError && required && shippingAddressFields[id].trim() === '';

  if (format) {
    hasFormatError =
      shippingAddressFields[id].trim() !== '' &&
      !format.test(shippingAddressFields[id]);
  }

  const showFormatError = canShowAnError && hasFormatError;

  let helperText;

  if (showRequiredError) {
    helperText = `The ${label.toLowerCase()} field is required.`;
  }

  if (showFormatError) {
    helperText = formatErrorMessage
      ? formatErrorMessage
      : `The ${label.toLowerCase()} field is invalid.`;
  }

  const showError = showRequiredError || showFormatError;
  const state = showError ? 'critical' : '';

  return (
    <TextInput
      helperText={helperText}
      id={id}
      label={label}
      onBlur={(event) => {
        setIsFocused(false);

        trackEvent(AnalyticsEvent.FIELD_ENTERED, {
          field: fieldEnteredFieldName,
          orderNumber: fieldEnteredOrderNumber,
          value: event.target.value.length.toString(),
        });

        if (onBlurAction) {
          onBlurAction(id);
        }
      }}
      onChange={(e) => {
        setShippingAddressFields({
          ...shippingAddressFields,
          [id]: e.target.value,
        });
      }}
      onFocus={() => {
        setHasBeenInteractedWith(true);
        setIsFocused(true);
      }}
      placeholder={placeholder}
      required={required}
      state={state}
      value={shippingAddressFields[id]}
    />
  );
};

export default ShippingAddressTextInput;
